import type { ContactMaterialTelemetry } from './contactMaterial';

export interface PhysicsPairTelemetryInput {
  isActive: boolean;
  bodyA: MatterJS.BodyType;
  bodyB: MatterJS.BodyType;
  collision: {
    parentA: MatterJS.BodyType;
    parentB: MatterJS.BodyType;
    depth: number;
    normal: MatterJS.Vector;
  };
  separation: number;
  contactCount: number;
  friction: number;
  frictionStatic: number;
  reverseAnimalTowerMaterial: ContactMaterialTelemetry;
  contacts: Array<{
    vertex: MatterJS.Vector;
    normalImpulse: number;
    tangentImpulse: number;
  }>;
}

export interface PhysicsTelemetrySnapshot {
  timestamp: number;
  bodies: Array<{
    id: number;
    animalId: string;
    x: number;
    y: number;
    angle: number;
    speed: number;
    angularSpeed: number;
    sleeping: boolean;
    sleepCounter: number;
    motion: number;
    totalContacts: number;
    positionImpulseX: number;
    positionImpulseY: number;
  }>;
  activePairs: Array<{
    bodyAId: number;
    bodyBId: number;
    partAId: number;
    partBId: number;
    partALabel: string;
    partBLabel: string;
    materialClass: ContactMaterialTelemetry['materialClass'];
    bodyAType: ContactMaterialTelemetry['bodyAType'];
    bodyBType: ContactMaterialTelemetry['bodyBType'];
    baseFriction: number;
    baseFrictionStatic: number;
    resolvedFriction: number;
    resolvedFrictionStatic: number;
    depth: number;
    separation: number;
    normalX: number;
    normalY: number;
    contactCount: number;
    contacts: Array<{
      x: number;
      y: number;
      normalImpulse: number;
      tangentImpulse: number;
    }>;
  }>;
}

export function snapshotPhysicsTelemetry(
  timestamp: number,
  animals: readonly { animalId: string; body: MatterJS.BodyType }[],
  pairs: readonly PhysicsPairTelemetryInput[],
): PhysicsTelemetrySnapshot {
  return {
    timestamp,
    bodies: animals.map(({ animalId, body }) => ({
      id: body.id,
      animalId,
      x: body.position.x,
      y: body.position.y,
      angle: body.angle,
      speed: body.speed,
      angularSpeed: body.angularSpeed,
      sleeping: body.isSleeping,
      sleepCounter: (body as MatterJS.BodyType & { sleepCounter: number }).sleepCounter,
      motion: body.motion,
      totalContacts: body.totalContacts,
      positionImpulseX: body.positionImpulse.x,
      positionImpulseY: body.positionImpulse.y,
    })),
    activePairs: pairs.filter((pair) => pair.isActive).map((pair) => ({
      bodyAId: pair.collision.parentA.id,
      bodyBId: pair.collision.parentB.id,
      partAId: pair.bodyA.id,
      partBId: pair.bodyB.id,
      partALabel: pair.bodyA.label,
      partBLabel: pair.bodyB.label,
      materialClass: pair.reverseAnimalTowerMaterial.materialClass,
      bodyAType: pair.reverseAnimalTowerMaterial.bodyAType,
      bodyBType: pair.reverseAnimalTowerMaterial.bodyBType,
      baseFriction: pair.reverseAnimalTowerMaterial.baseFriction,
      baseFrictionStatic: pair.reverseAnimalTowerMaterial.baseFrictionStatic,
      resolvedFriction: pair.reverseAnimalTowerMaterial.resolvedFriction,
      resolvedFrictionStatic: pair.reverseAnimalTowerMaterial.resolvedFrictionStatic,
      depth: pair.collision.depth,
      separation: pair.separation,
      normalX: pair.collision.normal.x,
      normalY: pair.collision.normal.y,
      contactCount: pair.contactCount,
      contacts: pair.contacts.slice(0, pair.contactCount).map((contact) => ({
        x: contact.vertex.x,
        y: contact.vertex.y,
        normalImpulse: contact.normalImpulse,
        tangentImpulse: contact.tangentImpulse,
      })),
    })),
  };
}
